import {
  useState,
  useEffect,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  Autocomplete,
  TextField,
  Box,
  Typography,
  Chip,
  Avatar,
  CircularProgress,
  Paper,
  Divider,
  FormControlLabel,
  Switch,
} from "@mui/material";
import {
  VpnKey,
  Person,
  Email,
  CheckCircle,
  Cancel,
  Warning,
  Block,
  Numbers,
} from "@mui/icons-material";
import HttpClient from "../../../helpers/HttpClient";

interface CursoData {
  code: string;
  name: string;
  category: string;
  chatEnabled: boolean;
  orden: number;
  imagen?: string;
  tipo?: string;
}

interface PinData {
  pin_id: number;
  pin: string;
  activaciones_permitidas: number;
  activaciones_realizadas: number;
  activaciones_restantes: number;
  meses_licencia: number;
  habilitado: boolean;
  estado: "activo" | "agotado" | "deshabilitado";
  fecha_asignacion_formatted?: string;
  pin_fecha_creacion_formatted?: string;
  total_usuarios_activaron: number;
  cursos: Array<CursoData>;
  usuarios: Array<{
    id: number;
    nombre: string;
    email: string;
    documento: string;
    fecha_nacimiento?: string;
    fecha_asignacion?: string;
    fecha_asignacion_formatted?: string;
    historial?: string;
  }>;
  usuario_principal?: {
    id: number;
    nombre: string;
    email: string;
    documento: string;
    fecha_nacimiento?: string;
    fecha_asignacion?: string;
    fecha_asignacion_formatted?: string;
    historial?: string;
  };
  lote_info?: {
    id: number;
    distribuidor: string;
  };
}

interface PinAutocompleteProps {
  onPinSelect?: (pin: PinData | null) => void;
  placeholder?: string;
  label?: string;
  disabled?: boolean;
  value?: PinData | null;
  fullWidth?: boolean;
  onlyActive?: boolean;
  showOnlyActiveToggle?: boolean;
}

export interface PinAutocompleteRef {
  clear: () => void;
}

// Hook personalizado para debounce
function useDebounce(value: string, delay: number) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

// Función para buscar PINEs
const searchPins = async (
  query: string,
  page = 0,
  limit = 20,
  onlyActive = false
) => {
  const params = new URLSearchParams({
    q: query,
    page: page.toString(),
    limit: limit.toString(),
    ...(onlyActive && { active: "true" }),
  });

  const result = await HttpClient().get(`/admin/pins/search?${params}`);

  if (result.status !== 200) {
    throw new Error(`Error ${result.status}: ${result.body}`);
  }

  return await result.data;
};

const PinAutocomplete = forwardRef<PinAutocompleteRef, PinAutocompleteProps>(
  (
    {
      onPinSelect,
      placeholder = "Buscar PIN por código, usuario, email o documento...",
      label = "Buscar PIN",
      disabled = false,
      value = null,
      fullWidth = true,
      onlyActive = false,
      showOnlyActiveToggle = true,
    },
    ref
  ) => {
    const [inputValue, setInputValue] = useState("");
    const [options, setOptions] = useState<PinData[]>([]);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const [page, setPage] = useState(0);
    const [selectedPin, setSelectedPin] = useState<PinData | null>(value);
    const [activeFilter, setActiveFilter] = useState(onlyActive);

    // Debounce del input
    const debouncedInputValue = useDebounce(inputValue, 300);

    // Exponer función "clear" al padre
    useImperativeHandle(ref, () => ({
      clear: () => {
        setSelectedPin(null);
        setInputValue("");
      },
    }));

    // Función para cargar PINEs
    const loadPins = useCallback(
      async (query: string, pageNum = 0, append = false) => {
        if (!query.trim() && pageNum === 0) {
          setOptions([]);
          setHasMore(false);
          return;
        }

        setLoading(true);

        try {
          const response = await searchPins(query, pageNum, 20, activeFilter);
          const newPins = response.data || [];

          if (append) {
            setOptions((prev) => [...prev, ...newPins]);
          } else {
            setOptions(newPins);
          }

          setHasMore(response.pagination?.hasMore || false);
          setPage(pageNum);
        } catch (error) {
          console.error("Error searching pins:", error);
          if (!append) {
            setOptions([]);
          }
          setHasMore(false);
        } finally {
          setLoading(false);
        }
      },
      [activeFilter]
    );

    // Cargar PINEs cuando cambie el término de búsqueda o el filtro
    useEffect(() => {
      if (debouncedInputValue.length >= 2) {
        loadPins(debouncedInputValue, 0, false);
      } else {
        setOptions([]);
        setHasMore(false);
        setPage(0);
      }
    }, [debouncedInputValue, loadPins]);

    // Función para cargar más resultados (scroll infinito)
    const loadMore = useCallback(() => {
      if (hasMore && !loading && debouncedInputValue.length >= 2) {
        loadPins(debouncedInputValue, page + 1, true);
      }
    }, [hasMore, loading, debouncedInputValue, page, loadPins]);

    // Función para obtener el color e icono del estado
    const getEstadoDisplay = (estado: PinData["estado"], habilitado: boolean) => {
      if (!habilitado) {
        return {
          color: "#EF4444",
          bgcolor: "#FEE2E2",
          icon: <Block sx={{ fontSize: 14 }} />,
          label: "DESHABILITADO",
        };
      }

      switch (estado) {
        case "activo":
          return {
            color: "#10B981",
            bgcolor: "#D1FAE5",
            icon: <CheckCircle sx={{ fontSize: 14 }} />,
            label: "ACTIVO",
          };
        case "agotado":
          return {
            color: "#F59E0B",
            bgcolor: "#FEF3C7",
            icon: <Warning sx={{ fontSize: 14 }} />,
            label: "AGOTADO",
          };
        default:
          return {
            color: "#6B7280",
            bgcolor: "#F3F4F6",
            icon: <Cancel sx={{ fontSize: 14 }} />,
            label: "INACTIVO",
          };
      }
    };

    // Componente personalizado para renderizar cada opción
    const renderOption = (props: any, option: PinData) => {
      const estadoDisplay = getEstadoDisplay(option.estado, option.habilitado);

      return (
        <Box component="li" {...props} key={option.pin_id}>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              width: "100%",
              py: 1,
            }}
          >
            <Avatar sx={{ mr: 2, bgcolor: "secondary.main", mt: 0.5 }}>
              <VpnKey />
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {/* PIN y Estado */}
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}
              >
                <Typography
                  variant="body1"
                  fontWeight="700"
                  sx={{ fontFamily: "monospace", fontSize: "1rem" }}
                >
                  {option.pin}
                </Typography>
                <Chip
                  label={estadoDisplay.label}
                  icon={estadoDisplay.icon}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: "0.7rem",
                    fontWeight: "bold",
                    color: estadoDisplay.color,
                    bgcolor: estadoDisplay.bgcolor,
                    "& .MuiChip-icon": {
                      color: estadoDisplay.color,
                    },
                  }}
                />
                {option.total_usuarios_activaron > 1 && (
                  <Chip
                    label={`${option.total_usuarios_activaron} usuarios`}
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: "0.65rem",
                      bgcolor: "info.light",
                      color: "info.contrastText",
                    }}
                  />
                )}
              </Box>

              {/* Usuario Principal */}
              {option.usuario_principal && (
                <>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 0.5,
                    }}
                  >
                    <Person sx={{ fontSize: 14, color: "text.secondary" }} />
                    <Typography variant="body2" fontWeight="600" noWrap>
                      {option.usuario_principal.nombre}
                      {option.total_usuarios_activaron > 1 && (
                        <>
                          <Typography
                            component="span"
                            variant="caption"
                            color="text.secondary"
                            sx={{ ml: 1 }}
                          >
                            (más reciente)
                          </Typography>
                          <Typography
                            component="span"
                            variant="body2"
                            color="text.secondary"
                            sx={{ ml: 1, fontWeight: 400 }}
                          >
                            y {option.total_usuarios_activaron - 1} más
                          </Typography>
                        </>
                      )}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 0.5,
                    }}
                  >
                    <Email sx={{ fontSize: 14, color: "text.secondary" }} />
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {option.usuario_principal.email}
                    </Typography>
                  </Box>
                </>
              )}

              {/* Usuarios adicionales (preview) */}
              {option.usuarios.length > 1 && (
                <Box
                  sx={{ ml: 2, mt: 0.5, pl: 1, borderLeft: "2px solid #E5E7EB" }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontWeight: 600 }}
                  >
                    Otros usuarios ({option.usuarios.length - 1}):
                  </Typography>
                  <Box
                    sx={{ display: "flex", gap: 0.5, mt: 0.5, flexWrap: "wrap" }}
                  >
                    {option.usuarios.slice(1, 3).map((usuario, index) => (
                      <Chip
                        key={index}
                        label={usuario.nombre.split(" ")[0]} // Solo primer nombre
                        size="small"
                        sx={{
                          height: 16,
                          fontSize: "0.6rem",
                          bgcolor: "grey.200",
                          color: "text.secondary",
                        }}
                      />
                    ))}
                    {option.usuarios.length > 3 && (
                      <Chip
                        label={`+${option.usuarios.length - 3}`}
                        size="small"
                        sx={{
                          height: 16,
                          fontSize: "0.6rem",
                          bgcolor: "grey.300",
                          color: "text.secondary",
                        }}
                      />
                    )}
                  </Box>
                </Box>
              )}

              {/* Información adicional */}
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 2, mt: 0.5 }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Numbers sx={{ fontSize: 12, color: "text.secondary" }} />
                  <Typography variant="caption" color="text.secondary">
                    {option.activaciones_realizadas}/
                    {option.activaciones_permitidas} usos
                  </Typography>
                </Box>
                {option.lote_info && (
                  <Typography variant="caption" color="text.secondary">
                    {option.lote_info.distribuidor}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      );
    };

    // Componente personalizado para el input
    const renderInput = (params: any) => (
      <Box>
        {showOnlyActiveToggle && (
          <FormControlLabel
            control={
              <Switch
                checked={activeFilter}
                onChange={(e) => setActiveFilter(e.target.checked)}
                size="small"
                disabled={loading}
              />
            }
            label="Solo PINEs activos"
            sx={{
              mb: 1,
              "& .MuiFormControlLabel-label": { fontSize: "0.8rem" },
            }}
          />
        )}
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          fullWidth={fullWidth}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading && <CircularProgress color="inherit" size={20} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      </Box>
    );

    // Componente personalizado para el dropdown
    const PaperComponent = (props: any) => (
      <Paper
        {...props}
        sx={{
          ...props.sx,
          maxHeight: 400,
          "& .MuiAutocomplete-listbox": {
            maxHeight: 350,
            "& .MuiAutocomplete-option:last-child": {
              borderBottom: hasMore ? "1px solid #e0e0e0" : "none",
            },
          },
        }}
      >
        {props.children}
        {hasMore && (
          <Box sx={{ p: 1, textAlign: "center" }}>
            <Divider sx={{ mb: 1 }} />
            <Typography
              variant="caption"
              color="primary"
              sx={{
                cursor: "pointer",
                "&:hover": { textDecoration: "underline" },
              }}
              onClick={loadMore}
            >
              {loading ? "Cargando más..." : "Cargar más resultados"}
            </Typography>
          </Box>
        )}
      </Paper>
    );

    const handleChange = (event: any, newValue: PinData | null) => {
      setSelectedPin(newValue);
      onPinSelect?.(newValue);
    };

    const handleInputChange = (event: any, newInputValue: string) => {
      setInputValue(newInputValue);
    };

    return (
      <Autocomplete
        value={selectedPin}
        onChange={handleChange}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        options={options}
        getOptionLabel={(option) =>
          `${option.pin} - ${
            option.usuario_principal?.nombre || "Sin activar por primera vez"
          }`
        }
        isOptionEqualToValue={(option, value) =>
          option.pin_id === value?.pin_id
        }
        renderOption={renderOption}
        renderInput={renderInput}
        PaperComponent={PaperComponent}
        loading={loading}
        disabled={disabled}
        noOptionsText={
          inputValue.length < 2
            ? "Escribe al menos 2 caracteres para buscar"
            : loading
            ? "Buscando..."
            : "No se encontraron PINEs"
        }
        sx={{
          flexGrow: 1,
          minWidth: 250,
          "& .MuiInputLabel-root": { fontSize: "0.8rem" },
          "& .MuiInputBase-input": { fontSize: "0.8rem" },
          "& .MuiOutlinedInput-root": {
            backgroundColor: "#F9FAFB",
          },
        }}
      />
    );
  }
);

export default PinAutocomplete;
